import { createSlice } from '@reduxjs/toolkit';

import { initializeAuth, login, resendCode, verifyMfa } from './auth-api';
import { logout } from './logout';
import type { User } from '@/types/auth';

export interface AuthState {
    user: User | null;
    isLoggedIn: boolean;
    isInitializing: boolean;
    pendingEmail: string | null;
    pendingPassword: string | null;
    session: string | null;
    /** Cognito MFA challenge from login/mfa (e.g. EMAIL_OTP, SOFTWARE_TOKEN_MFA). */
    challengeName: string | null;
}

const initialState: AuthState = {
    user: null,
    isLoggedIn: false,
    isInitializing: true,
    pendingEmail: null,
    pendingPassword: null,
    session: null,
    challengeName: null
};

const resetAuthSession = (state: AuthState) => {
    state.user = null;
    state.isLoggedIn = false;
    state.isInitializing = false;
    state.pendingEmail = null;
    state.pendingPassword = null;
    state.session = null;
    state.challengeName = null;
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearMfaState: (state) => {
            state.pendingEmail = null;
            state.session = null;
            state.challengeName = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(logout, (state) => {
                resetAuthSession(state);
            })
            .addCase(initializeAuth.pending, (state) => {
                state.isInitializing = true;
            })
            .addCase(initializeAuth.fulfilled, (state, { payload }) => {
                state.user = payload.user;
                state.isLoggedIn = payload.isLoggedIn;
                state.isInitializing = false;
                state.pendingEmail = null;
                state.session = null;
                state.challengeName = null;
            })
            .addCase(initializeAuth.rejected, (state) => {
                resetAuthSession(state);
            })

            .addCase(login.fulfilled, (state, { payload }) => {
                state.user = null;
                state.isLoggedIn = false;
                state.isInitializing = false;
                state.pendingEmail = payload.email;
                state.pendingPassword = payload.password;
                state.session = payload.session;
                state.challengeName = payload.challengeName;
            })
            .addCase(login.rejected, (state) => {
                resetAuthSession(state);
            })
            .addCase(resendCode.fulfilled, (state, { payload }) => {
                state.session = payload.session;
            })
            .addCase(verifyMfa.fulfilled, (state, { payload }) => {
                state.user = payload.user;
                state.isLoggedIn = true;
                state.isInitializing = false;
                state.pendingEmail = null;
                state.session = null;
                state.challengeName = null;
            });
    }
});

export const { clearMfaState } = authSlice.actions;
export { logout };
export default authSlice.reducer;
